import { useQuery } from '@tanstack/react-query';
import { AuthNonceHeader, type Points, ProfilePoints } from '@/api/spec.ts';
import { environment } from '../../../environment.ts';
import useAppStore from '../../../store/rootStore.ts';
import { getValidNonce } from '../../../store/slices/authSlice.ts';

const EMPTY_POINTS: Points = {
  at: {},
  total_points: '0',
  total_referral_bonus: '0',
  total_rank: 0,
  last_epoch_emission: '0',
  d: [],
};

/** Shared so other consumers (e.g. tournament eligibility) hit the same cache entry. */
export const profilePointsQueryKey = (authNonce: string | undefined) => ['profile-points', authNonce] as const;

export const fetchProfilePoints = async (authNonce: string): Promise<Points> => {
  const response = await fetch(`${environment.apiUrl}${ProfilePoints}`, {
    headers: {
      [AuthNonceHeader]: authNonce,
    },
  });

  if (response.status === 404) {
    return EMPTY_POINTS;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch Points');
  }

  return response.json();
};

export const useProfilePoints = () => {
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const authNonce = useAppStore((state) => getValidNonce(state.authNonce)?.value);
  const pointsEnabled = useAppStore((state) => state.config?.features?.pointsEnabled);

  return useQuery<Points>({
    queryKey: profilePointsQueryKey(authNonce),
    queryFn: () => fetchProfilePoints(authNonce!),
    enabled: isFullyAuthorized && Boolean(authNonce) && pointsEnabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
};
